import React from "react";
import { useSelector } from "react-redux";
import { Link } from "gatsby";
import { Anchor, Flex, Text, Tag, EllipsisLoader } from "@pacerpro/shared-ui";
import {
  selectUsersMeta,
  selectDistributionListsMeta,
  selectTracksMeta,
  selectUsersNavLoading,
  selectDistributionListsNavLoading,
  selectTracksNavLoading,
} from "../../selectors";

const NavText: React.FunctionComponent<{ active?: boolean }> = ({
  children,
  active,
}) => {
  return (
    <Text
      fontSize={3}
      mr={2}
      color={active ? "black" : "primary"}
      fontWeight={active ? "medium" : undefined}
      underline={active && true}
    >
      {children}
    </Text>
  );
};

const NavTag: React.FunctionComponent<{ active?: boolean }> = ({
  children,
  active,
}) => {
  return (
    <Tag
      size="md"
      radius="full"
      sx={{ fontWeight: "medium", bg: active ? "black" : "primary" }}
    >
      {children}
    </Tag>
  );
};

interface NavItem {
  label: string;
  to: string;
  active?: boolean;
}

const data: NavItem[] = [
  { label: "Users", to: "users" },
  { label: "Litigation", to: "litigation" },
  { label: "Tracks", to: "tracks" },
  { label: "Organization", to: "organization" },
];

interface Props {
  active?: string;
}

const Navigation: React.FunctionComponent<Props> = ({ active }) => {
  const usersMeta = useSelector(selectUsersMeta);
  const usersLoading = useSelector(selectUsersNavLoading);
  const distributionListsMeta = useSelector(selectDistributionListsMeta);
  const distributionListsLoading = useSelector(
    selectDistributionListsNavLoading,
  );
  const tracksMeta = useSelector(selectTracksMeta);
  const tracksLoading = useSelector(selectTracksNavLoading);

  const counts = React.useMemo<{ [key: string]: number }>(() => {
    return {
      Users: usersMeta.recordCount,
      Litigation: distributionListsMeta.recordCount,
      Tracks: tracksMeta.recordCount,
    };
  }, [usersMeta, distributionListsMeta, tracksMeta]);

  const loadingStates = React.useMemo<{ [key: string]: boolean }>(() => {
    return {
      Users: usersLoading,
      Litigation: distributionListsLoading,
      Tracks: tracksLoading,
    };
  }, [usersLoading, distributionListsLoading, tracksLoading]);

  return (
    <Flex mt={7}>
      {data.map(({ label, to }) => {
        const isActive = to === active;
        const count = counts[label];
        const isLoading = loadingStates[label];
        const renderTag = () => {
          if (to !== "organization") {
            if (isLoading) {
              return (
                <NavTag active={isActive}>
                  <EllipsisLoader size={5} color="#ffffff" />
                </NavTag>
              );
            } else if (count > 0) {
              return <NavTag active={isActive}>{count}</NavTag>;
            }
          }
        };
        return (
          <Flex align="center" mr={5} key={label}>
            <Anchor as={Link} to={`/${to}`} hover={false}>
              <NavText active={isActive}>{label}</NavText>
              {renderTag()}
            </Anchor>
          </Flex>
        );
      })}
    </Flex>
  );
};

export default Navigation;
